import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import EquipoService from '../../services/EquipoServiceFirestore';
import { SocioServiceFirestore, Socio } from '../../services/SocioServiceFirestore';
import { AppContext } from '../../context/AppContext';
import { FaPlus, FaEdit, FaTrash, FaUsers } from '../../components/Icons';
import { FaEuroSign } from 'react-icons/fa';
import { theme } from '../../theme';

interface Equipo {
  id?: string;
  nombre: string;
  descripcion?: string;
  socios: string[]; // array de ids de socios
  imagenCoche?: string; // URL de la imagen del coche/skin
}

// Componentes estilizados
const Container = styled.div`
  padding: 24px;
  max-width: 1280px;
  margin: 0 auto;
  background-color: ${theme.colors.background.default};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
`;

const Title = styled.h1`
  color: ${theme.colors.primary.main};
  font-weight: ${theme.typography.fontWeights.bold};
  font-size: 2.2rem;
  position: relative;
  padding-bottom: 12px;
  display: flex;
  align-items: center;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 60px;
    height: 4px;
    background-color: ${theme.colors.secondary.main};
    border-radius: 2px;
  }
  
  svg {
    margin-right: 16px;
    color: ${theme.colors.secondary.main};
  }
`;

const Button = styled.button`
  background-color: ${theme.colors.secondary.main};
  color: ${theme.colors.secondary.contrastText};
  border: none;
  border-radius: ${theme.borderRadius.small};
  padding: 12px 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  font-weight: ${theme.typography.fontWeights.medium};
  transition: all 0.3s ease;
  box-shadow: ${theme.shadows.small};
  
  &:hover {
    background-color: ${theme.colors.secondary.dark};
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.medium};
  }
  
  svg {
    margin-right: 10px;
    font-size: 1.1rem;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
  gap: 24px;
`;

const Card = styled.div`
  background-color: ${theme.colors.background.paper};
  border-radius: ${theme.borderRadius.medium};
  padding: 24px;
  box-shadow: ${theme.shadows.medium};
  display: flex;
  flex-direction: column;
  gap: 16px;
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.05);
  overflow: hidden;
  
  &:hover {
    box-shadow: ${theme.shadows.large};
    transform: translateY(-2px);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CardTitle = styled.h2`
  margin: 0;
  font-size: 1.4rem;
  color: ${theme.colors.primary.main};
  font-weight: ${theme.typography.fontWeights.semibold};
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 12px;
    color: ${theme.colors.secondary.main};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  background-color: transparent;
  border: none;
  cursor: pointer;
  color: ${theme.colors.primary.main};
  font-size: 1.1rem;
  padding: 8px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    color: ${theme.colors.secondary.main};
    background-color: rgba(0, 0, 0, 0.05);
    transform: translateY(-2px);
  }
`;

const Description = styled.div`
  color: ${theme.colors.text.secondary};
  margin-bottom: 8px;
  font-size: 0.95rem;
  line-height: 1.5;
`;

const EmptyText = styled.span`
  color: ${theme.colors.text.disabled};
  font-style: italic;
`;

const SociosList = styled.div`
  font-size: 0.95rem;
  color: ${theme.colors.text.primary};
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SociosTitle = styled.div`
  font-weight: ${theme.typography.fontWeights.semibold};
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 8px;
    color: ${theme.colors.secondary.main};
  }
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 32px;
  background-color: ${theme.colors.background.paper};
  border-radius: ${theme.borderRadius.medium};
  box-shadow: ${theme.shadows.medium};
  color: ${theme.colors.text.secondary};
  font-size: 1.1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  
  svg {
    font-size: 3rem;
    color: ${theme.colors.secondary.main};
    opacity: 0.7;
  }
`;

// Styled components para el valor de mercado
const ValorMercadoSection = styled.div`
  margin-top: 16px;
  padding: 16px;
  background: linear-gradient(135deg, #0f3460 0%, #e4312e 100%);
  border-radius: 8px;
  color: white;
`;

const ValorMercadoTitle = styled.div`
  font-weight: ${theme.typography.fontWeights.semibold};
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  font-size: 1rem;
  
  svg {
    margin-right: 8px;
    color: #fff;
  }
`;

const ValorTotal = styled.div`
  font-size: 1.4rem;
  font-weight: bold;
  text-align: center;
  margin-bottom: 12px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
`;

const PilotoValor = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  font-size: 0.9rem;
  
  &:last-child {
    border-bottom: none;
  }
`;

const NombrePiloto = styled.span`
  flex: 1;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const ValorIndividual = styled.span`
  font-weight: ${theme.typography.fontWeights.semibold};
  margin-left: 8px;
`;

const EquiposList: React.FC = () => {
  const [equipos, setEquipos] = useState<Equipo[]>([]);
  const [socios, setSocios] = useState<Socio[]>([]);
  const navigate = useNavigate();
  const { data } = useContext(AppContext);

  useEffect(() => {
    EquipoService.getAll().then(setEquipos);
    SocioServiceFirestore.getAll().then(setSocios);
  }, []);

  // Función para calcular las estadísticas de un piloto
  const calcularEstadisticasPiloto = (socio: Socio) => {
    if (!socio || !data) return null;

    // Obtener carreras del piloto
    const carreras = data.carreras.filter(c => c.publicada && c.resultados.some(r => r.socioId === socio.id));
    const sanciones = data.sanciones?.filter(s => s.socioId === socio.id) || [];

    let carrerasCompletadas = 0;
    let victorias = 0;
    let podios = 0;
    let abandonos = 0;
    let sancionesRecibidas = sanciones.length;
    let totalPosicionesCarrera = 0;
    let carrerasConPosicionValida = 0;

    carreras.forEach((carrera) => {
      const resultado = carrera.resultados.find(r => r.socioId === socio.id);
      if (resultado) {
        carrerasCompletadas++;
        
        if (resultado.posicion === 1) victorias++;
        if (resultado.posicion && resultado.posicion <= 3) podios++;
        if (resultado.dnf) abandonos++;
        
        if (resultado.posicion && !resultado.dnf) {
          totalPosicionesCarrera += resultado.posicion;
          carrerasConPosicionValida++;
        }
      }
    });

    const posicionMediaCarrera = carrerasConPosicionValida > 0 ? totalPosicionesCarrera / carrerasConPosicionValida : 0;
    
    // Obtener posición en la liga
    const { posicionGeneral, totalPilotosEnLiga } = obtenerPosicionEnLiga(socio);
    
    // Calcular reputación
    const reputacion = calcularReputacion({
      carrerasCompletadas,
      victorias,
      podios,
      abandonos,
      sancionesRecibidas,
      posicionMediaCarrera,
      posicionGeneral,
      totalPilotosEnLiga
    });

    return { reputacion };
  };

  // Función para obtener posición en la liga
  const obtenerPosicionEnLiga = (piloto: Socio) => {
    if (!data) return { posicionGeneral: 0, totalPilotosEnLiga: 0 };

    const categoria = piloto.Categoria;
    const ligaDelPiloto = data.ligas.find(liga => categoria && liga.nombre.includes(categoria));
    
    const pilotosEnLiga = data.socios.filter(s => s.Categoria === categoria);
    
    if (pilotosEnLiga.length === 0) {
      return { posicionGeneral: 1, totalPilotosEnLiga: 1 };
    }

    // Calcular puntos de cada piloto
    const pilotosConPuntos = pilotosEnLiga.map((p: Socio) => {
      let puntosTotales = 0;
      data.carreras.forEach(carrera => {
        if (carrera.publicada) {
          const resultado = carrera.resultados.find(r => r.socioId === p.id);
          if (resultado) {
            puntosTotales += resultado.puntos || 0;
          }
        }
      });
      return { ...p, puntosTotalesCalculados: puntosTotales };
    });

    const pilotosOrdenados = [...pilotosConPuntos].sort((a: any, b: any) => {
      return (b.puntosTotalesCalculados || 0) - (a.puntosTotalesCalculados || 0);
    });

    const posicion = pilotosOrdenados.findIndex((p: any) => p.id === piloto.id) + 1;
    
    return {
      posicionGeneral: posicion > 0 ? posicion : pilotosOrdenados.length,
      totalPilotosEnLiga: pilotosOrdenados.length
    };
  };

  // Función para calcular la reputación
  const calcularReputacion = (stats: {
    carrerasCompletadas: number;
    victorias: number;
    podios: number;
    abandonos: number;
    sancionesRecibidas: number;
    posicionMediaCarrera: number;
    posicionGeneral: number;
    totalPilotosEnLiga: number;
  }) => {
    if (stats.carrerasCompletadas === 0) return 50;

    // Factor de posición (40%)
    let factorPosicion = 40;
    if (stats.totalPilotosEnLiga > 1 && stats.posicionGeneral > 1) {
      const porcentajePosicion = (stats.posicionGeneral - 1) / (stats.totalPilotosEnLiga - 1);
      factorPosicion = Math.round(40 * (1 - porcentajePosicion));
    }

    // Factor de resultados (40%)
    let factorResultados = 20;
    const ratioVictorias = stats.carrerasCompletadas > 0 ? stats.victorias / stats.carrerasCompletadas : 0;
    const ratioPodiosNoVictorias = stats.carrerasCompletadas > 0 ? 
      Math.max(0, (stats.podios - stats.victorias) / stats.carrerasCompletadas) : 0;
    const ratioAbandonos = stats.carrerasCompletadas > 0 ? stats.abandonos / stats.carrerasCompletadas : 0;

    factorResultados += Math.round(ratioVictorias * 20);
    factorResultados += Math.round(ratioPodiosNoVictorias * 15);
    factorResultados -= Math.round(ratioAbandonos * 15);
    factorResultados = Math.max(0, Math.min(40, factorResultados));

    // Factor de sanciones (20%)
    let factorSanciones = 20;
    if (stats.carrerasCompletadas > 0 && stats.sancionesRecibidas > 0) {
      const ratioSanciones = stats.sancionesRecibidas / stats.carrerasCompletadas;
      factorSanciones = Math.max(0, 20 - Math.round(ratioSanciones * 15));
    }

    return Math.min(100, factorPosicion + factorResultados + factorSanciones);
  };

  // Función para calcular el valor de mercado de un piloto
  const calcularValorMercado = (socio: Socio) => {
    const stats = calcularEstadisticasPiloto(socio);
    if (!stats) return 0;
    
    const reputacion = stats.reputacion || 0;
    const sueldoBase = 84000;
    return Math.round(sueldoBase * (reputacion / 100));
  };

  // Función para formatear el precio
  const formatearPrecio = (precio: number) => {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(precio);
  };

  const handleDelete = (id: string, nombre: string) => {
    if (window.confirm(`¿Estás seguro de que quieres eliminar el equipo ${nombre}?`)) {
      EquipoService.delete(id).then(() => {
        EquipoService.getAll().then(setEquipos);
      });
    }
  };

  return (
    <Container>
      <Header>
        <Title><FaUsers /> Equipos</Title>
        <Button onClick={() => navigate('/equipos/new')}>
          <FaPlus /> Nuevo Equipo
        </Button>
      </Header>
      
      {equipos.length > 0 ? (
        <Grid>
          {equipos.map(equipo => (
            <Card key={equipo.id}>
              {/* Imagen del equipo */}
              {equipo.imagenCoche && (
                <div style={{
                  height: '180px',
                  margin: '-24px -24px 16px -24px',
                  overflow: 'hidden',
                  position: 'relative',
                  backgroundColor: '#f5f6fa',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderBottom: '1px solid #eee'
                }}>
                  <img 
                    src={equipo.imagenCoche} 
                    alt={`Coche de ${equipo.nombre}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }}
                  />
                </div>
              )}
              
              <CardHeader>
                <CardTitle><FaUsers /> {equipo.nombre}</CardTitle>
                <ButtonGroup>
                  <ActionButton 
                    onClick={() => navigate(`/equipos/${equipo.id}`)}
                    title="Editar equipo"
                  >
                    <FaEdit />
                  </ActionButton>
                  <ActionButton 
                    onClick={() => handleDelete(equipo.id!, equipo.nombre)}
                    title="Eliminar equipo"
                  >
                    <FaTrash />
                  </ActionButton>
                </ButtonGroup>
              </CardHeader>
              
              <Description>
                {equipo.descripcion || <EmptyText>Sin descripción</EmptyText>}
              </Description>
              
              <SociosList>
                <SociosTitle><FaUsers /> Socios:</SociosTitle>
                {equipo.socios.length === 0 ? (
                  <EmptyText>Sin socios</EmptyText>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {equipo.socios.map((socioId) => {
                      const socio = socios.find(s => s.id === socioId);
                      const nombre = socio ? (socio.display_name || socio.NOMBRE_CORTO || socio.nombre) : '-';
                      return (
                        <div 
                          key={socioId}
                          onClick={() => navigate(`/socios/profile/${socioId}`)}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            backgroundColor: '#f5f6fa',
                            padding: '4px 12px',
                            borderRadius: '20px',
                            fontSize: '0.9rem',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            border: '1px solid #eee'
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = '#e9ecef';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor = '#f5f6fa';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}
                        >
                          {nombre}
                        </div>
                      );
                    })}
                  </div>
                )}
              </SociosList>

              {/* Sección de Valor de Mercado */}
              {equipo.socios.length > 0 && (
                <ValorMercadoSection>
                  <ValorMercadoTitle>
                    <FaEuroSign /> Valor de Mercado
                  </ValorMercadoTitle>
                  
                  {/* Calcular y mostrar valor total */}
                  {(() => {
                    let valorTotal = 0;
                    const pilotosConValor = equipo.socios.map(socioId => {
                      const socio = socios.find(s => s.id === socioId);
                      if (socio) {
                        const valor = calcularValorMercado(socio);
                        valorTotal += valor;
                        return {
                          nombre: socio.display_name || socio.NOMBRE_CORTO || socio.nombre || 'Sin nombre',
                          valor
                        };
                      }
                      return { nombre: 'Sin nombre', valor: 0 };
                    });

                    return (
                      <>
                        <ValorTotal>
                          Total: {formatearPrecio(valorTotal)}
                        </ValorTotal>
                        
                        {/* Listado individual */}
                        {pilotosConValor.map((piloto, index) => (
                          <PilotoValor key={index}>
                            <NombrePiloto>{piloto.nombre}</NombrePiloto>
                            <ValorIndividual>{formatearPrecio(piloto.valor)}</ValorIndividual>
                          </PilotoValor>
                        ))}
                      </>
                    );
                  })()}
                </ValorMercadoSection>
              )}
            </Card>
          ))}
        </Grid>
      ) : (
        <EmptyMessage>
          <FaUsers />
          <span>No hay equipos disponibles</span>
        </EmptyMessage>
      )}
    </Container>
  );
};

export default EquiposList;

